import {
  IconLogout,
} from "@tabler/icons-react"
import { HiOutlineUserCircle } from "react-icons/hi2"

import { useAuth } from "@/hooks/useAuth"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import Avatar from "@/components/Avatar"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
    initials?: string
  }
}) {
  const { isMobile, state } = useSidebar()
  const { logout } = useAuth()
  const isExpanded = state === "expanded"

  return (
    <SidebarMenu className="relative">
      {/* Horizontal Divider above Profile section */}
      <div className="-mx-2 mb-1">
        <Separator className="bg-sidebar-border w-[calc(100%+16px)]" />
      </div>

      <SidebarMenuItem className="relative">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className={cn(
                "cursor-pointer",
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                "group-data-[collapsible=icon]:!p-2",
                isExpanded && "w-[calc(100%-32px)]" // Narrower button to prevent hover background overlap
              )}
            >
              <HiOutlineUserCircle className="size-5 shrink-0" />
              {isExpanded && (
                <span className="truncate font-medium">
                  Profile
                </span>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar src={user.avatar} alt={user.name} fallback={user.initials || "CN"} size="md" className="rounded-md" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <IconLogout />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Trigger inline when expanded - positioned at right of profile button */}
        {isExpanded && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2 z-20">
            <SidebarTrigger className="size-8 [&_svg]:size-4" />
          </div>
        )}
      </SidebarMenuItem>

      {/* Separator and Trigger - shown when collapsed */}
      {!isExpanded && (
        <>
          <div className="-mx-2 my-1">
            <Separator className="bg-sidebar-border w-[calc(100%+16px)]" />
          </div>
          <SidebarMenuItem>
            <SidebarTrigger className="w-full justify-center [&_svg]:size-4" />
          </SidebarMenuItem>
        </>
      )}
    </SidebarMenu>
  )
}
